import { ProKanbanClient, ApiError } from '@pro-kanban/sdk';

const STORAGE_KEY = 'pro-kanban.tokens';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
}

function readTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(tokens: StoredTokens | null): void {
  if (tokens) localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  else localStorage.removeItem(STORAGE_KEY);
}

/**
 * Single SDK instance shared by the whole app.
 *
 * Token rotation is handled inside the SDK; this wrapper only persists the
 * resulting pair and notifies the auth store when the session is gone.
 */
export const client = new ProKanbanClient({
  baseUrl: __API_BASE_URL__,
  realtimeUrl: __API_ORIGIN__,
  autoConnectRealtime: true,
  timeoutMs: 30_000,
  // Lets the SDK see a rotation another tab performed before it replays the
  // pair this tab has been holding since the page loaded.
  loadTokens: readTokens,
  onTokensRefreshed: (tokens) => {
    writeTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, sessionId: tokens.sessionId });
  },
  onUnauthorized: () => {
    writeTokens(null);
    // Also drops the realtime socket; the SDK has already cleared its own pair.
    void client.setTokens(null);
    window.dispatchEvent(new CustomEvent('pro-kanban:signed-out'));
  },
});

export const api = client.http;

// `storage` only fires in the *other* tabs, so this is how a background tab
// learns that the session was rotated — or ended — somewhere else. Without it
// that tab keeps a superseded refresh token and trips the server's
// token-reuse detection, which revokes the session for all of them.
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  const tokens = readTokens();
  if (tokens) {
    void client.setTokens(tokens);
  } else {
    void client.setTokens(null);
    window.dispatchEvent(new CustomEvent('pro-kanban:signed-out'));
  }
});

/** Restores the session saved by a previous visit. */
export async function restoreSession(): Promise<boolean> {
  const tokens = readTokens();
  if (!tokens) return false;
  await client.setTokens(tokens);
  return true;
}

export async function persistSession(tokens: StoredTokens | null): Promise<void> {
  writeTokens(tokens);
  await client.setTokens(tokens);
}

export function currentSessionId(): string | null {
  return readTokens()?.sessionId ?? null;
}

export interface FileRequestOptions {
  thumbnail?: boolean;
  width?: number;
}

/** Fetches protected file content through the authenticated HTTP client. */
export function fetchFile(fileId: string, options: FileRequestOptions = {}): Promise<Blob> {
  return api.get<Blob>(`files/${fileId}/content`, {
    thumb: options.thumbnail ? 'true' : undefined,
    w: options.width,
  });
}

/** Hands the browser a blob to save, whatever produced it. */
export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick rather than immediately: Safari cancels a download
  // whose object URL is gone before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadFile(fileId: string, name: string): Promise<void> {
  downloadBlob(await api.get<Blob>(`files/${fileId}/content`, { download: 'true' }), name);
}

export { ApiError };
export type { StoredTokens as SessionTokens };
